import React, { useState } from 'react';
import { ValidCell } from '../types';
import { BUILDINGS } from '../constants';
import { getDynamicBuildingScore } from '../utils/dynamicScore';
import { useTheme } from '../contexts/ThemeContext';
import { useTranslation } from 'react-i18next';
import { AnimatePresence } from 'framer-motion';
import ScoreTooltip from './ScoreTooltip';
import ShopIcon from './ShopIcon';
import BuildingIcon from './BuildingIcon';

interface GameBoardProps {
  chosenLocationsA: ValidCell[];
  chosenLocationsB: ValidCell[];
  currentSelections: {
    teamA?: ValidCell | null;
    teamB?: ValidCell | null;
  };
  blockedPositions?: ValidCell[];
  masterBlockedPositions?: ValidCell[];
  competitorSharesMarket?: boolean;
  board?: {
    [key in ValidCell]?: 'A' | 'B';
  };
  onCellClick?: (cell: ValidCell) => void;
  clickableForTeam?: boolean;
  financeMode?: boolean;
  shopPriceMultipliers?: { [key in ValidCell]?: number };
}

const GameBoard: React.FC<GameBoardProps> = ({
  chosenLocationsA,
  chosenLocationsB,
  currentSelections,
  blockedPositions = [],
  masterBlockedPositions = [],
  competitorSharesMarket = false,
  board = {},
  onCellClick,
  clickableForTeam = false,
  financeMode = false,
  shopPriceMultipliers = {}
}) => {
  const { theme } = useTheme();
  const { t } = useTranslation();
  const [hoveredCell, setHoveredCell] = useState<ValidCell | null>(null);
  const [tooltipPosition, setTooltipPosition] = useState<{ x: number; y: number }>({ x: 0, y: 0 });
  const [tooltipPlacement, setTooltipPlacement] = useState<{ vertical: 'top' | 'bottom'; horizontal: 'left' | 'right' | 'center' }>({ vertical: 'top', horizontal: 'center' });

  // Calculate shop cost (client-side mirror of server logic)
  const calculateShopCost = (cell: ValidCell): number => {
    if (!financeMode) return 0;

    // Identify adjacent buildings
    let adjacentBuildings: { id: string, score: number }[] = [];

    for (const [id, building] of Object.entries(BUILDINGS)) {
      if ((building.adjacent as readonly string[]).includes(cell)) {
        adjacentBuildings.push({ id, score: building.score });
      }
    }

    // Sum potential (scores of adjacent buildings)
    const totalPotential = adjacentBuildings.reduce((sum, b) => sum + b.score, 0);

    // Base Cost = 50% of sum
    let cost = totalPotential * 0.5;

    // Count occupied neighbors
    const gridRows = [
      ['M1', 'M2', 'M3', 'M4', 'M5'],
      ['K1', 'K2', 'K3', 'K4', 'K5'],
      ['D1', 'D2', 'D3', 'D4', 'D5'],
      ['L1', 'L2', 'L3', 'L4', 'L5'],
      ['W1', 'W2', 'W3', 'W4', 'W5']
    ];

    let occupiedNeighbors = 0;
    let r = -1, c = -1;

    // Find cell coordinates
    for (let i = 0; i < gridRows.length; i++) {
      const idx = gridRows[i].indexOf(cell);
      if (idx !== -1) {
        r = i;
        c = idx;
        break;
      }
    }

    if (r !== -1 && c !== -1) {
      const neighbors = [
        { r: r, c: c - 1 }, { r: r, c: c + 1 }, // Horizontal
        { r: r - 1, c: c }, { r: r + 1, c: c }  // Vertical
      ];

      neighbors.forEach(n => {
        if (n.r >= 0 && n.r < 5 && n.c >= 0 && n.c < 5) {
          const neighborCell = gridRows[n.r][n.c] as ValidCell;
          // Check if occupied by Team A, Team B, or Competitor
          // Note: board contains 'A' or 'B'. masterBlockedPositions contains cells.
          if (board[neighborCell] || masterBlockedPositions.includes(neighborCell)) {
            occupiedNeighbors++;
          }
        }
      });
    }

    // Multiplier = 1 + (0.10 * occupied_neighbors_count)
    cost = cost * (1 + (0.10 * occupiedNeighbors));

    // Check for conflict penalty multiplier
    if (shopPriceMultipliers[cell]) {
      cost = cost * shopPriceMultipliers[cell]!;
    }

    return Math.round(cost);
  };

  const renderSalesPoint = (cell: string) => {
    const isChosenA = chosenLocationsA.includes(cell as ValidCell);
    const isChosenB = chosenLocationsB.includes(cell as ValidCell);
    const isCurrentA = currentSelections.teamA === cell;
    const isCurrentB = currentSelections.teamB === cell;
    const isBlocked = blockedPositions.includes(cell as ValidCell);
    const isMasterBlocked = masterBlockedPositions.includes(cell as ValidCell);
    const isOccupied = isChosenA || isChosenB || isMasterBlocked;
    const isClickable = clickableForTeam && !isOccupied && !isBlocked && onCellClick;
    const isHovered = hoveredCell === cell;

    // Determine State and Team for the Icon
    let status: 'occupied' | 'selected' | 'hover' | 'neutral' | 'blocked' | 'competitor' = 'neutral';
    let team: 'A' | 'B' | 'none' = 'none';

    if (isChosenA) {
      status = 'occupied';
      team = 'A';
    } else if (isChosenB) {
      status = 'occupied';
      team = 'B';
    } else if (isCurrentA) {
      status = 'selected';
      team = 'A';
    } else if (isCurrentB) {
      status = 'selected';
      team = 'B';
    } else if (isMasterBlocked) {
      status = 'competitor';
    } else if (isBlocked) {
      status = 'blocked';
    } else if (isHovered && isClickable) {
      status = 'hover';
    }

    const handleMouseEnter = (e: React.MouseEvent) => {
      if (!isOccupied && !isBlocked) {
        setHoveredCell(cell as ValidCell);
        const rect = e.currentTarget.getBoundingClientRect();

        // Dynamic placement based on viewport position
        const viewportHeight = window.innerHeight;
        const viewportWidth = window.innerWidth;

        // Vertical: If in bottom half of screen (> 60%), force TOP placement.
        // Otherwise default to BOTTOM (or TOP if very close to bottom edge).
        // Using 60% threshold gives a bit more preference to bottom placement which is more natural,
        // unless we are really low.
        const isBottomHalf = rect.top > (viewportHeight * 0.6);
        const vertical = isBottomHalf ? 'top' : 'bottom';

        // Horizontal: Check distance to edges
        // If closer to left edge (< 100px), align LEFT
        // If closer to right edge (< 100px remaining), align RIGHT
        // Else CENTER
        let horizontal: 'left' | 'right' | 'center' = 'center';
        if (rect.left < 100) horizontal = 'left';
        else if (viewportWidth - rect.right < 100) horizontal = 'right';

        const y = vertical === 'bottom' ? rect.bottom : rect.top;

        setTooltipPosition({
          x: rect.left + rect.width / 2,
          y: y
        });

        setTooltipPlacement({ vertical, horizontal });
      }
    };

    const handleClick = () => {
      if (isClickable) {
        onCellClick(cell as ValidCell);
      }
    };

    return (
      <div
        className={`w-12 h-12 flex items-center justify-center relative transition-all duration-300 ${status === 'selected' ? 'animate-pulse scale-110' : ''}`}
        style={{
          transform: isHovered && isClickable ? 'scale(1.2)' : 'scale(1)',
          cursor: isClickable ? 'pointer' : 'default',
          zIndex: isHovered || status === 'selected' ? 20 : 1
        }}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={() => setHoveredCell(null)}
        onClick={handleClick}
      >
        <ShopIcon
          status={status}
          team={team}
          className="w-full h-full drop-shadow-md"
        />

        {/* Label Text */}
        <span
          className="absolute -top-1 left-0 right-0 font-bold text-[10px] text-center z-20 text-white"
          style={{
            textShadow: '0 2px 4px rgba(0,0,0,0.8), 0 0 4px rgba(0,0,0,0.5)',
            fontWeight: '900',
            opacity: status === 'neutral' ? 0.7 : 1
          }}
        >
          {cell}
        </span>
      </div>
    );
  };

  // Map base score to building variant (1-4)
  const getBuildingVariant = (baseScore: number): 1 | 2 | 3 | 4 => {
    if (baseScore >= 60) return 1;
    if (baseScore >= 48) return 2;
    if (baseScore >= 24) return 3;
    return 4;
  };

  const renderBuilding = (cell: string, baseScore: number) => {
    // Calculate dynamic score
    const dynamicScore = getDynamicBuildingScore(cell, {
      chosenLocationsA,
      chosenLocationsB,
      masterBlockedPositions: masterBlockedPositions || [],
      competitorSharesMarket,
      board
    });

    // Format score (show decimals if needed)
    const displayScore = dynamicScore % 1 === 0
      ? dynamicScore.toString()
      : dynamicScore.toFixed(2);

    const variant = getBuildingVariant(baseScore);

    return (
      <div
        className="
          w-12 h-12
          flex items-center justify-center
          relative
          transition-all duration-200
        "
        title={t('board.tooltip.buildingTooltip', { cell, score: displayScore, base: baseScore.toString() })}
      >
        <BuildingIcon
          variant={variant}
          className="w-full h-full drop-shadow-lg"
          score={baseScore}
        />
        {/* Display score centered in yellow */}
        <div
          className="absolute inset-0 flex items-center justify-center font-bold text-[10px] text-center z-10 text-yellow-300"
          style={{
            textShadow: '0 1px 2px rgba(0,0,0,0.95), 0 0 5px rgba(0,0,0,0.8)',
            marginTop: '2px' // Slight optical adjustment
          }}
        >
          {displayScore}
        </div>
      </div>
    );
  };

  // Create a 9x9 grid representation
  const grid = Array(9).fill(null).map(() => Array(9).fill(null));

  // Place sales points (cells) on even coordinates
  const salesPoints = [
    ['M1', 'M2', 'M3', 'M4', 'M5'],
    ['K1', 'K2', 'K3', 'K4', 'K5'],
    ['D1', 'D2', 'D3', 'D4', 'D5'],
    ['L1', 'L2', 'L3', 'L4', 'L5'],
    ['W1', 'W2', 'W3', 'W4', 'W5']
  ];

  // Fill cells (even coordinates)
  salesPoints.forEach((row, rowIndex) => {
    row.forEach((cell, colIndex) => {
      grid[rowIndex * 2][colIndex * 2] = renderSalesPoint(cell);
    });
  });

  // Place buildings (squares) on odd coordinates
  const buildingRows = [
    ['U1', 'U2', 'U3', 'U4'],
    ['X1', 'X2', 'X3', 'X4'],
    ['Y1', 'Y2', 'Y3', 'Y4'],
    ['Z1', 'Z2', 'Z3', 'Z4']
  ];

  // Fill buildings (odd coordinates)
  buildingRows.forEach((row, rowIndex) => {
    row.forEach((building, colIndex) => {
      grid[rowIndex * 2 + 1][colIndex * 2 + 1] = renderBuilding(
        building,
        BUILDINGS[building as keyof typeof BUILDINGS]?.score || 0
      );
    });
  });

  return (
    <div className="relative w-full max-w-[800px] mx-auto">
      <div className="bg-gradient-to-br from-gray-100 to-gray-200 dark:from-gray-800 dark:to-gray-900 rounded-xl shadow-2xl p-8 border border-gray-300 dark:border-gray-700">
        <div className="grid grid-cols-9 gap-2">
          {grid.map((row, rowIndex) => (
            <React.Fragment key={rowIndex}>
              {row.map((cell, colIndex) => (
                <div key={`${rowIndex}-${colIndex}`} className="flex justify-center items-center">
                  {cell}
                </div>
              ))}
            </React.Fragment>
          ))}
        </div>
      </div>

      {/* Score Tooltip on Hover */}
      <AnimatePresence>
        {hoveredCell && !chosenLocationsA.includes(hoveredCell) && !chosenLocationsB.includes(hoveredCell) && !masterBlockedPositions.includes(hoveredCell) && (
          <ScoreTooltip
            position={hoveredCell}
            x={tooltipPosition.x}
            y={tooltipPosition.y}
            currentTeam="A"
            teamAPositions={chosenLocationsA}
            teamBPositions={chosenLocationsB}
            competitorPositions={masterBlockedPositions}
            cost={calculateShopCost(hoveredCell)}
            financeMode={financeMode}
            placement={tooltipPlacement}
          />
        )}
      </AnimatePresence>
    </div>
  );
};

export default GameBoard;
